import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { DraggableData, DraggableEvent } from 'react-draggable';

export interface ModalPosition {
  readonly x: number;
  readonly y: number;
}

export interface DraggableModalBounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface UseDraggableModalPositionOptions {
  readonly initialOffset?: ModalPosition | (() => ModalPosition);
  readonly rightInsetPx?: number;
}

export interface UseDraggableModalPositionResult {
  readonly position: ModalPosition;
  readonly bounds: DraggableModalBounds;
  readonly modalRef: RefObject<HTMLDivElement>;
  readonly onDragStop: (event: DraggableEvent, data: DraggableData) => void;
}

const DEFAULT_RIGHT_INSET_PX = 480;
const DEFAULT_OFFSET: ModalPosition = { x: 0, y: 0 };

export function readNavbarWidthRem(): number {
  return document.querySelector('.xa-navbar.expanded') ? 27.5 : 4.8;
}

export function readNavbarClearancePx(): number {
  return readNavbarWidthRem() * 10;
}

export function readRightSidebarWidthPx(): number {
  const toolbar = document.querySelector('.xa-toolbar');
  return toolbar instanceof HTMLElement ? toolbar.offsetWidth : 300;
}

export function readSurveyDataViewerInitialOffset(): ModalPosition {
  return { x: 0, y: 40 };
}

export function clampModalPositionX(
  x: number,
  minX: number,
  maxX: number,
): number {
  if (!Number.isFinite(x)) {
    return Number.isFinite(minX) ? minX : 0;
  }
  if (Number.isFinite(maxX) && x > maxX) {
    return maxX;
  }
  if (Number.isFinite(minX) && x < minX) {
    return minX;
  }
  return x;
}

export function positionFromClearance(
  clearancePx: number,
  offset: ModalPosition,
): ModalPosition {
  const slackX = Number.isFinite(offset.x) ? Math.max(0, offset.x) : 0;
  const y = Number.isFinite(offset.y) ? offset.y : 0;
  return {
    x: clearancePx + slackX,
    y,
  };
}

export function slackFromAbsoluteX(
  absoluteX: number,
  clearancePx: number,
): number {
  if (!Number.isFinite(absoluteX)) {
    return 0;
  }
  return Math.max(0, absoluteX - clearancePx);
}

function resolveInitialOffset(
  initialOffset: UseDraggableModalPositionOptions['initialOffset'],
): ModalPosition {
  if (typeof initialOffset === 'function') {
    return initialOffset();
  }
  return initialOffset ?? DEFAULT_OFFSET;
}

export function useDraggableModalPosition(
  options: UseDraggableModalPositionOptions = {},
): UseDraggableModalPositionResult {
  const { initialOffset, rightInsetPx = DEFAULT_RIGHT_INSET_PX } = options;

  const modalRef = useRef<HTMLDivElement>(null);
  const userOffsetRef = useRef<ModalPosition>(
    resolveInitialOffset(initialOffset),
  );
  const [clearancePx, setClearancePx] = useState<number>(readNavbarClearancePx);
  const [rightSidebarWidth, setRightSidebarWidth] = useState<number>(
    readRightSidebarWidthPx,
  );
  const [position, setPosition] = useState<ModalPosition>(() =>
    positionFromClearance(
      readNavbarClearancePx(),
      resolveInitialOffset(initialOffset),
    ),
  );

  const syncToChrome = useCallback(() => {
    const nextClearancePx = readNavbarClearancePx();
    setClearancePx(nextClearancePx);
    setRightSidebarWidth(readRightSidebarWidthPx());
    setPosition(
      positionFromClearance(nextClearancePx, userOffsetRef.current),
    );
  }, []);

  useEffect(() => {
    syncToChrome();
    window.addEventListener('resize', syncToChrome);
    const navbar = document.querySelector('.xa-navbar');
    const observer =
      navbar instanceof Element
        ? new MutationObserver(syncToChrome)
        : null;
    if (navbar instanceof Element && observer) {
      observer.observe(navbar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    return () => {
      window.removeEventListener('resize', syncToChrome);
      observer?.disconnect();
    };
  }, [syncToChrome]);

  const onDragStop = useCallback(
    (_event: DraggableEvent, data: DraggableData): void => {
      const maxX = window.innerWidth - rightSidebarWidth - rightInsetPx;
      const clampedX = clampModalPositionX(data.x, clearancePx, maxX);
      const nextOffset: ModalPosition = {
        x: slackFromAbsoluteX(clampedX, clearancePx),
        y: data.y,
      };
      userOffsetRef.current = nextOffset;
      setPosition(positionFromClearance(clearancePx, nextOffset));
    },
    [clearancePx, rightInsetPx, rightSidebarWidth],
  );

  const bounds: DraggableModalBounds = {
    left: clearancePx,
    top: 0,
    bottom: window.innerHeight - (modalRef.current?.offsetHeight || 0) - 20,
    right: window.innerWidth - rightSidebarWidth - rightInsetPx,
  };

  return {
    position,
    bounds,
    modalRef,
    onDragStop,
  };
}
